import { Link, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx";
import { NotificationTypes, addNotification } from "../../stores/notification-store"
import { UpdateComponentConfig } from "../../utils/api"

import useComponentConfigData from "../../hooks/useComponentConfigData.js"
import useComponentConfigSchema, { componentConfigSchema } from "../../hooks/useComponentConfigSchema.js"
import useUtgData from "../../hooks/useUtgData";

export default function ApiInterfaceList() {

    const { beId, componentId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()

    const { utg, loading: utgLoading, error: utgError } = useUtgData(beId, componentId, config, accessToken)
    const { loading: componentConfigSchemaLoading } = useComponentConfigSchema(beId, utg.value.UtgVersion, config, accessToken)
    const apiInterfaceDropdownItems = useComputed(() => {
        if (componentConfigSchema.value.config) {
            const apiInterfaceItems = componentConfigSchema.value?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items ?? []
            const apiInterfaceOptions = apiInterfaceItems.map(s => {
                const apiInterfaceName = s.properties.cdType["s4-caption"]
                const id = s.properties.cdType["s4-model-key"]
                return {
                    apiInterfaceName,
                    id
                }
            })
            return apiInterfaceOptions
        }
        return []
    })
    const { componentConfigData, loading: componentConfigLoading, modified } = useComponentConfigData(beId, utg.value, config, accessToken)
    const apiInterfaces = useComputed(() => {
        if (componentConfigData.value.componentConfig) {
            // extract apiInterface from componentConfigData for ease of use
            const interfaces = componentConfigData.value?.componentConfig?.config?.apiInterfaceConfig?.apiInterface ?? []
            return interfaces
        }
        return []
    })

    const showDeleteModal = useSignal(false)
    const confirmCdType = useSignal('')
    const confirmTaskDesc = useSignal('')
    const confirmListenPort = useSignal('')
    const accordionData = [
        { name: 'API Interfaces', data: apiInterfaces },
        { name: 'Component Config', data: componentConfigData },
        { name: 'API Interfaces Dropdown data', data: apiInterfaceDropdownItems },
        { name: 'UTG Data', data: utg },
        { name: 'Component Schema', data: componentConfigSchema },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    function ApiInterfaceTableRows({ apiInterfaces }) {
        if (apiInterfaces && apiInterfaces.length == 0) {
            return (
                <tbody><tr><td colSpan="4" className="text-center">This UTG does not have any configured API Interfaces</td></tr></tbody>
            )
        }

        let rows = []
        apiInterfaces.forEach(apiInterface => {
            rows.push(
                <ApiInterfaceRow apiInterface={apiInterface} key={`${apiInterface.cdType}${apiInterface.listenPort}`} />
            )
        })

        return (
            <tbody>{rows}</tbody>
        )
    }

    function ApiInterfaceRow({ apiInterface }) {
        const cdType = apiInterface.cdType
        const taskDesc = apiInterface.taskDesc
        const listenPort = apiInterface.listenPort
        return (
            <tr>
                <td><Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}>{apiInterface.taskDesc}</Link></td>
                <td>{apiInterface.cdType}</td>
                <td>{apiInterface.listenPort}</td>
                <td>
                {canEditSignal.value && <Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}>
                    <Button variant="outline-primary" size="sm"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button>
                </Link>}
                {canEditSignal.value && <Button variant="outline-danger" size="sm" className="mx-1" onClick={() => showModal(cdType, taskDesc, listenPort)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    function deleteApiInterface() {
        showDeleteModal.value = false

        // filter out apiInterface to be deleted
        const filteredApiInterfaces = componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface.filter(apiInterface =>
            !(apiInterface.listenPort == confirmListenPort.value));

        // set newly filtered apiInterface array 
        componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface = filteredApiInterfaces

        //build data object for updateComponentConfig request
        let data = {
            config: componentConfigData.value.componentConfig.config,
            hash: componentConfigData.value.componentConfig.hash,
            isCommitted: componentConfigData.value.componentConfig.isCommitted,
            lastUpdatedAt: componentConfigData.value.componentConfig.lastUpdatedAt,
            lastUpdatedBy: componentConfigData.value.componentConfig.lastUpdatedBy,
            revisionId: componentConfigData.value.componentConfig.revisionId,
            componentId: componentConfigData.value.componentConfig.componentId
        }

        console.log(data)

        UpdateComponentConfig(beId, utg.value.MachinePermanentId, utg.value.ComponentPermanentId, data, config, accessToken)
            .then(result => {
                console.log('successfully updated component config')
                addNotification('The Api Interface was successfully removed.', 'success', true)
                modified.value = true
                componentConfigData.value = data
                window.location.reload()
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString(), NotificationTypes.ERROR, false)
                return false
            })
            .finally(() => {
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (cdType, taskDesc, listenPort) => {
        // just set the state and the modal will appear
        confirmCdType.value = cdType
        confirmTaskDesc.value = taskDesc
        confirmListenPort.value = listenPort
        showDeleteModal.value = true
    }

    function NewApiInterfaceDropdown() {
        // only allow one Oracle Interface per UTG
        let oracleApiInterfaces = apiInterfaces.value.filter( apiInterface => {
            return apiInterface.cdType == 148
        })

        let apiInterfaceOptions = apiInterfaceDropdownItems.value.map(s => {
            let isDisabled = false
            
            if (s.id == 148 && oracleApiInterfaces.length) {
                isDisabled = true
            }

            return (
                <Dropdown.Item href={`/be/${beId}/utgs/${componentId}/apiinterfaces/${s.id}`} key={s.id} disabled={isDisabled}>{s.apiInterfaceName}</Dropdown.Item>
            )
        })

        return (
            <DropdownButton id="dropdown-add-apiinterface"
                variant="outline-secondary"
                size="sml"
                title="New API Interface"
                className="mx-3">
                {apiInterfaceOptions}
            </DropdownButton>
        )
    }

    if (utgLoading.value || componentConfigLoading.value || componentConfigSchemaLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        {canEditSignal.value && <ConfirmationModal show={showDeleteModal.value} handleCancel={() => showDeleteModal.value = false} handleConfirm={() => deleteApiInterface()} message={`Are you sure you want to delete ${confirmTaskDesc.value}? This action cannot be undone.`} title={`Deleting ${confirmTaskDesc.value}`} />}
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                UTG List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {utg.value.ComponentNickname}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='UTG API Interfaces' canEditSignal={canEditSignal} />
            <UtgTools componentConfigData={componentConfigData.value}></UtgTools>
        </Stack>
        <UtgNav beId={beId} componentId={componentId} activeKey="2"></UtgNav>

        <Table responsive striped hover size="sm">
            <thead>
                <tr>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Port</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <ApiInterfaceTableRows apiInterfaces={apiInterfaces.value} />
        </Table>
        {canEditSignal.value && <NewApiInterfaceDropdown />}
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}